"use client";

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getTechMetrics,
  getDailyTrend,
  getRejectBreakdown,
  getPlaybookDistribution,
  listPlaybooks,
  type TechMetrics,
  type DailyTrendItem,
  type RejectBreakdownItem,
  type PlaybookDistributionItem,
  type PlaybookSummary,
} from "@/lib/api";
import { cn, today, aggregateTrend, type DatePreset } from "@/lib/utils";
import { StatCard } from "@/components/stat-card";
import { DatePresetPicker } from "@/components/date-preset-picker";
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const SOURCES = ["", "outbound", "freeswitch", "batch", "api"] as const;
const SOURCE_LABELS: Record<string, string> = {
  "": "全部来源",
  outbound: "外呼",
  freeswitch: "FreeSWITCH",
  batch: "批次",
  api: "API",
};

const WATERFALL_STAGES = [
  { key: "ttfb_asr_ms", label: "ASR", color: "#38bdf8" },
  { key: "ttfb_pilot_ms", label: "决策模型", color: "#8b5cf6" },
  { key: "ttfb_talker_ms", label: "回复模型", color: "#34d399" },
  { key: "ttfb_tts_ms", label: "TTS", color: "#fbbf24" },
] as const;

const GPU_ROWS = [
  { key: "gpu_asr_ms", label: "ASR" },
  { key: "gpu_llm_ms", label: "LLM" },
  { key: "gpu_tts_ms", label: "TTS" },
] as const;

const OUTCOMES = [
  { field: "completed" as const, label: "已完成", color: "#34d399" },
  { field: "noAnswer" as const, label: "未接听", color: "#fbbf24" },
  { field: "busy" as const, label: "忙线", color: "#f87171" },
  { field: "amd" as const, label: "AMD", color: "#8b5cf6" },
] as const;


export default function DashboardPage() {
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [activePreset, setActivePreset] = useState<DatePreset>("today");
  const [source, setSource] = useState("");
  const [playbookId, setPlaybookId] = useState("");
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [data, setData] = useState<TechMetrics | null>(null);
  const [trend, setTrend] = useState<DailyTrendItem[]>([]);
  const [rejectItems, setRejectItems] = useState<RejectBreakdownItem[]>([]);
  const [playbookDist, setPlaybookDist] = useState<PlaybookDistributionItem[]>([]);
  const [techOpen, setTechOpen] = useState(true);

  useEffect(() => {
    listPlaybooks().then((r) => setPlaybooks(r.playbooks));
  }, []);

  useEffect(() => {
    getTechMetrics(startDate, endDate, source, playbookId).then(setData);
    getDailyTrend(startDate, endDate, source, playbookId).then((r) => setTrend(r.days));
    getRejectBreakdown(startDate, endDate, source, playbookId).then((r) => setRejectItems(r.items));
  }, [startDate, endDate, source, playbookId]);

  useEffect(() => {
    if (playbookId) return;
    getPlaybookDistribution(startDate, endDate, source).then((r) => setPlaybookDist(r.items));
  }, [startDate, endDate, source, playbookId]);

  // --- Business aggregates + pie data from trend ---
  const biz = useMemo(() => {
    const base = aggregateTrend(trend);
    let amd = 0, noAnswer = 0, busy = 0;
    for (const d of trend) {
      amd += d.amd_detected;
      noAnswer += d.no_answer;
      busy += d.busy;
    }
    const counts = { completed: base.completed, noAnswer, busy, amd } as const;
    const pieData = OUTCOMES
      .map((o) => ({ name: o.label, value: counts[o.field], color: o.color }))
      .filter((i) => i.value > 0);
    return { ...base, amd, noAnswer, busy, pieData };
  }, [trend]);

  // --- Trend chart data ---
  const trendChartData = useMemo(
    () => trend.map((d) => ({
      date: d.date.slice(5), // MM-DD
      total: d.total,
      answer_rate: d.total > 0 ? Math.round((d.answered / d.total) * 100) : 0,
    })),
    [trend],
  );

  // --- TTFB table (also used for waterfall chart) ---
  const ttfbTableData = useMemo(() => {
    if (!data) return [];
    const m = data.metrics;
    let prevP50 = 0;
    let prevP95 = 0;
    return WATERFALL_STAGES.map((s) => {
      const cumP50 = m[s.key]?.p50 ?? 0;
      const cumP95 = m[s.key]?.p95 ?? 0;
      const deltaP50 = Math.max(0, cumP50 - prevP50);
      const deltaP95 = Math.max(0, cumP95 - prevP95);
      prevP50 = cumP50;
      prevP95 = cumP95;
      return { name: s.label, color: s.color, deltaP50, deltaP95, cumP50 };
    });
  }, [data]);

  const e2eTtfbP50 = data?.metrics.ttfb_tts_ms?.p50 ?? 0;
  const e2eTtfbP95 = data?.metrics.ttfb_tts_ms?.p95 ?? 0;

  return (
    <div className="flex min-h-full flex-col p-6">
      {/* Row 0: Header + Filters */}
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-balance">数据概览</h1>
        <div className="flex items-center gap-4">
          <DatePresetPicker
            active={activePreset}
            onSelect={(key, s, e) => { setStartDate(s); setEndDate(e); setActivePreset(key); }}
          />

          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
            className="rounded-md border px-2 py-1 text-sm"
          />
          <span className="text-sm text-muted-foreground">~</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
            className="rounded-md border px-2 py-1 text-sm"
          />
          <Select value={source || "__all__"} onValueChange={(v) => setSource(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="全部来源" />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => (
                <SelectItem key={s || "__all__"} value={s || "__all__"}>
                  {SOURCE_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={playbookId || "__all__"} onValueChange={(v) => setPlaybookId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="全部话术" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全部话术</SelectItem>
              {playbooks.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data && (
            <span className="text-sm text-muted-foreground">
              {biz.total} 次对话 · {data.sessions} 个会话 · {data.turns} 轮
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-6">
        {/* Row 1: Hero Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard large title="对话总数" value={biz.total.toLocaleString()} sub={`已接通 ${biz.answered.toLocaleString()} 次`} />

          {/* Conversion Funnel */}
          <Card>
            <CardContent className="px-6 pt-5 pb-4">
              <p className="text-sm text-muted-foreground">转化漏斗</p>
              <div className="mt-3 flex flex-col gap-2.5">
                {([
                  { label: "接通", pct: biz.answerRate, num: biz.answered, den: biz.total, color: "#60a5fa" },
                  { label: "完成", pct: biz.completeRate, num: biz.completed, den: biz.answered, color: "#34d399" },
                  { label: "成功", pct: biz.successRate, num: biz.success, den: biz.answered, color: "#f59e0b" },
                ]).map((row) => (
                  <div key={row.label}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="tabular-nums font-medium">
                        {row.den > 0 ? `${row.pct.toFixed(1)}%` : "-"}
                        <span className="ml-1.5 font-normal text-muted-foreground">{row.num}/{row.den}</span>
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                      <div
                        className="h-full rounded-full transition-[width]"
                        style={{ width: `${row.den > 0 ? row.pct : 0}%`, backgroundColor: row.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <StatCard large title="平均对话轮数" value={data?.turns_per_session ? String(data.turns_per_session) : "-"} />
          <StatCard
            large
            title="端到端首响应延迟 P50"
            value={e2eTtfbP50 ? `${e2eTtfbP50}ms` : "-"}
            sub={`P95: ${e2eTtfbP95 ? `${e2eTtfbP95}ms` : "-"}`}
          />
        </div>

        {/* Row 2: Call Volume & Answer Rate Trend */}
        {trendChartData.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">对话量与接通率趋势</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={trendChartData} margin={{ left: 0, right: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="total" name="对话" fill="#60a5fa" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" dataKey="answer_rate" name="接通率 %" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Row 3: Outcomes donut + TTFB Waterfall */}
        <div className="grid grid-cols-2 gap-4">
          {/* Call Outcomes Donut */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">对话结果</CardTitle>
            </CardHeader>
            <CardContent>
              {biz.pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={biz.pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      {biz.pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [Number(value).toLocaleString(), "对话"]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无对话结果数据</p>
              )}
            </CardContent>
          </Card>

          {/* TTFB Waterfall */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">首响应延迟瀑布图（P50）</CardTitle>
            </CardHeader>
            <CardContent>
              {ttfbTableData.some((d) => d.deltaP50 > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={[{ name: "TTFB" }]} layout="vertical" margin={{ left: 0, right: 24 }}>
                      <XAxis type="number" unit="ms" />
                      <YAxis type="category" dataKey="name" width={0} tick={false} />
                      <Tooltip formatter={(value) => [`${value}ms`]} />
                      {ttfbTableData.map((d) => (
                        <Bar key={d.name} dataKey={() => d.deltaP50} name={d.name} stackId="ttfb" fill={d.color}>
                          <Cell fill={d.color} />
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                    {ttfbTableData.map((d) => (
                      <span key={d.name} className="flex items-center gap-1">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} />
                        {d.name}: {d.deltaP50}ms
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无首响应延迟数据</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 3.5: Reject Breakdown */}
        {rejectItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">拒绝原因</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(160, rejectItems.length * 32 + 40)}>
                <BarChart data={rejectItems} layout="vertical" margin={{ left: 120, right: 24 }}>
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="reject_reason" tick={{ fontSize: 12 }} width={120} />
                  <Tooltip
                    formatter={(value) => [`${value} 次`]}
                  />
                  <Bar dataKey="count" fill="#f87171" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {!playbookId && playbookDist.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">话术分布</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(160, playbookDist.length * 32 + 40)}>
                <BarChart
                  data={playbookDist.map((p) => ({
                    name: p.display_name || p.playbook_id,
                    answered: p.answered,
                    unanswered: Math.max(0, p.total - p.answered),
                  }))}
                  layout="vertical"
                  margin={{ left: 120, right: 24 }}
                >
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={120} />
                  <Tooltip formatter={(value, n) => [`${value} 次`, n]} />
                  <Legend />
                  <Bar dataKey="answered" name="已接通" stackId="pb" fill={OUTCOMES[0].color} />
                  <Bar dataKey="unanswered" name="未接通" stackId="pb" fill="#cbd5e1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Row 4: Tech Details (collapsible) */}
        {data && data.turns > 0 && (
          <div>
            <button
              onClick={() => setTechOpen(!techOpen)}
              className="mb-4 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className={cn("transition-transform", techOpen ? "rotate-90" : "")}>&#9654;</span>
              技术指标
            </button>
            {techOpen && (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* TTFB Stage Detail */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">各阶段首响应延迟</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="px-3 py-1.5 text-left font-medium">阶段</th>
                            <th className="px-3 py-1.5 text-right font-medium">阶段耗时 P50</th>
                            <th className="px-3 py-1.5 text-right font-medium">阶段耗时 P95</th>
                            <th className="px-3 py-1.5 text-right font-medium">累计耗时 P50</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ttfbTableData.map((row) => (
                            <tr key={row.name} className="border-b last:border-0">
                              <td className="px-3 py-1.5 font-medium">{row.name}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">
                                {row.deltaP50 ? `${row.deltaP50}ms` : "-"}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">
                                {row.deltaP95 ? `${row.deltaP95}ms` : "-"}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">
                                {row.cumP50 ? `${row.cumP50}ms` : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  {/* GPU Stage Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">各阶段 GPU 耗时</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="px-3 py-1.5 text-left font-medium">阶段</th>
                            <th className="px-3 py-1.5 text-right font-medium">GPU 耗时 P50</th>
                            <th className="px-3 py-1.5 text-right font-medium">GPU 耗时 P95</th>
                          </tr>
                        </thead>
                        <tbody>
                          {GPU_ROWS.map(({ key, label }) => {
                            const m = data.metrics[key];
                            return (
                              <tr key={key} className="border-b last:border-0">
                                <td className="px-3 py-1.5 font-medium">{label}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {m?.p50 ? `${m.p50}ms` : "-"}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums">
                                  {m?.p95 ? `${m.p95}ms` : "-"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <StatCard
                    title="ASR RTF P50"
                    value={data.derived.asr_rtf.p50 ? String(data.derived.asr_rtf.p50) : "-"}
                  />
                  <StatCard
                    title="TTS RTF P50"
                    value={data.derived.tts_rtf.p50 ? String(data.derived.tts_rtf.p50) : "-"}
                  />
                  <StatCard
                    title="模型回复耗时 P50"
                    value={data.metrics.llm_duration_ms?.p50 ? `${data.metrics.llm_duration_ms.p50}ms` : "-"}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {data && data.turns === 0 && biz.total === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">该时间段暂无数据。</p>
      )}
    </div>
  );
}
